// ABOUTME: Handles CV upload and parser handoff for applicant creation.
// ABOUTME: Preserves manual entry when parsing is unavailable and waits for async parser results.
import React from "react";
import { createStyles, Theme, makeStyles } from "@material-ui/core/styles";
import Loading from "customHooks/useCompleoForm/helpers/Loading";
import { Field } from "formik";
import { FieldProps } from "formik";
import { useAuthorization, useAuthState } from "_ReactContext/AuthContext";
import { Grid } from "@material-ui/core";
import { navigate } from "@gatsbyjs/reach-router";
import { clearAccentuatedStringFileUpload, toBase64 } from "functions/util";
import {
    apiDirectCall,
    PARSER_DIRECT_TIMEOUT_MS
} from "customHooks/useApi/api";
import LinearProgress, {
    LinearProgressProps
} from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import File from "customHooks/useCompleoForm/Inputs/File/File";
import useApplicantNewContext from "../ApplicantNewEdit/useApplicantNewContext/useApplicantNewContext";
import useShowMessage from "customHooks/useShowMessage/useShowMessage";
import AlertUpgradePlan from "customHooks/useFeatureUpgradePlan/AlertUpgradePlan";
import {
    buildManualEntryPayload,
    isRecoverableParserFailure
} from "./ApplicantUploadFallback";
import {
    ParserUploadTimeoutError,
    waitForParserUploadResults
} from "./parserUploadReadiness";

const useStylesLocal = makeStyles({
    root: {
        width: "100%"
    }
});

function LinearProgressWithLabel(
    props: LinearProgressProps & { value: number }
) {
    const classes = useStylesLocal();
    return (
        <div className={classes.root}>
            <Box display="flex" alignItems="center">
                <Box width="100%" mr={1}>
                    <LinearProgress variant="determinate" {...props} />
                </Box>
                <Box minWidth={35}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                    >{`${Math.round(props.value)}%`}</Typography>
                </Box>
            </Box>
        </div>
    );
}

interface IProps {
    metadata: any;
    className: any;
    t: any;
    classes: any;
    required: boolean;
    label: string;
    requiredMetadata: boolean;
    helperTextDefault: null | string;
}

const UploadComponent = (props: IProps & FieldProps) => {
    const {
        label,
        field,
        t,
        helperTextDefault,
        metadata,
        requiredMetadata,
        ...other
    } = props;
    const [, dispatchData] = useApplicantNewContext();
    const showMessage = useShowMessage();
    const { isFeatureAuthorized } = useAuthorization();

    const limitOneCVByPlan = isFeatureAuthorized("BulkParser_Upgrade");

    metadata.fileDefinition.maxFiles = limitOneCVByPlan
        ? 1
        : metadata.fileDefinition.maxFiles;
    // limitOneCVByPlan

    const [uploading, setUploading] = React.useState<boolean | number>(false);
    const [waitingForParser, setWaitingForParser] = React.useState(false);
    const parserWaitController = React.useRef(new AbortController());
    const { company } = useAuthState();

    React.useEffect(
        () => () => {
            parserWaitController.current.abort();
        },
        []
    );

    const calculatePercentual = (
        actualOperation: number,
        numberOfOperations: number
    ) => {
        return Math.floor((actualOperation / numberOfOperations) * 100);
    };

    const isPdfFile = (file: globalThis.File) => {
        return (
            file.type === "application/pdf" ||
            file.name.toLowerCase().endsWith(".pdf")
        );
    };

    const createPdfUrlFromBase64 = (base64data: string) => {
        const binary = atob(base64data);
        const buffer = new ArrayBuffer(binary.length);
        const view = new Uint8Array(buffer);
        for (let index = 0; index < binary.length; index++) {
            view[index] = binary.charCodeAt(index);
        }

        return URL.createObjectURL(
            new Blob([view], { type: "application/pdf" })
        );
    };

    const uploadFileToSignedUrl = async (
        file: globalThis.File,
        returnData: any
    ) => {
        const originalFileName = clearAccentuatedStringFileUpload(file.name);
        await apiDirectCall(
            returnData.uploadURL,
            "put",
            file,
            {
                headers: {
                    "Content-Type": returnData.contentType || file.type,
                    ...(returnData.cacheControl
                        ? { "Cache-Control": returnData.cacheControl }
                        : {}),
                    "x-amz-tagging": `originalFileName=${originalFileName}`
                }
            },
            true,
            false
        );
    };

    const uploadOneFile = async (acceptedFiles: any[]) => {
        setUploading(30);
        const file = acceptedFiles[0];
        const fileNameCleared = clearAccentuatedStringFileUpload(file.name);
        const urlPDF = isPdfFile(file) ? URL.createObjectURL(file) : undefined;

        try {
            const base64 = await toBase64(file);
            setUploading(60);

            const axiosReturnParser = await apiDirectCall(
                "/parser/parserdirect",
                "post",
                {
                    base64File: base64,
                    fileName: fileNameCleared,
                    returnPDF: true,
                    companyId: company.companyId.toString()
                },
                {
                    timeout: PARSER_DIRECT_TIMEOUT_MS
                }
            );

            if (isRecoverableParserFailure(axiosReturnParser.data)) {
                dispatchData({
                    type: "update",
                    payload: buildManualEntryPayload(file, urlPDF)
                });
                return;
            }

            if (!axiosReturnParser.data?.data) {
                throw new Error(
                    axiosReturnParser.data?.message || "CV parser failed"
                );
            }

            setUploading(90);
            const parsedPdfUrl =
                axiosReturnParser.data.base64data &&
                createPdfUrlFromBase64(axiosReturnParser.data.base64data);
            dispatchData({
                type: "update",
                payload: {
                    jsonParsedCV: axiosReturnParser.data.data,
                    urlCV: parsedPdfUrl || urlPDF,
                    pageToRender: "verificationData",
                    cvFile: { streamContent: file, fileName: file.name }
                }
            });
        } catch (ex) {
            showMessage({
                message: t("NotIdentifiedProblem"),
                snackBarOrign: { vertical: "top", horizontal: "right" },
                timeout: 1000 * 10,
                typeMessage: "error"
            });
            setUploading(false);
        }
    };

    const onDropFunction = async (
        acceptedFiles: any[],
        rejectedFiles: any[]
    ) => {
        try {
            let actualOperation = 0;
            setUploading(actualOperation);
            if (acceptedFiles.length === 0) {
                setUploading(false);
                return;
            }
            if (rejectedFiles.length > 0) {
                setUploading(false);
                return;
            }
            if (acceptedFiles.length === 1) {
                await uploadOneFile(acceptedFiles);
                return;
            }

            const numberOfOperations = (acceptedFiles.length || 0) + 2.05;

            const re = /(?:\.([^.]+))?$/;
            const filesAndExtensions: Compleo.IObject = [];
            const valueDefinition: CompleoShared.ParserOperation.IParsedItem[] =
                [];
            const keyFieldName = "parserUpload";

            for (const file of acceptedFiles) {
                const extension = (re.exec(file.name) || [])[1];
                // const fileNameCleared = clearString(clearAccentuatedString(file.name), "_");
                const originalFileName = clearAccentuatedStringFileUpload(
                    file.name
                );
                filesAndExtensions.push({
                    extension: extension,
                    originalFileName: originalFileName,
                    fieldName: keyFieldName,
                    contentType: file.type
                });
            }

            const axPost = await apiDirectCall(
                "/general/requests3url",
                "post",
                {
                    files: filesAndExtensions,
                    companyId: company.companyId || 0
                }
            );
            actualOperation++;
            setUploading(
                calculatePercentual(actualOperation, numberOfOperations)
            );
            const urls = axPost.data;

            // helper function to process a single file
            const processFile = async (file: any): Promise<any> => {
                const originalFileName = clearAccentuatedStringFileUpload(
                    file.name
                );
                const returnData = urls.find(
                    (u: any) =>
                        u.originalFileName === originalFileName &&
                        u.fieldName === keyFieldName
                );
                const filename = returnData.fileName;
                await uploadFileToSignedUrl(file, returnData);
                return {
                    cv: {
                        key: filename,
                        originalFileName: file.name
                    }
                };
            };

            const batchSize = 10;
            let completedOperations = 0;
            let failedFilesCount = 0;
            const results: any[] = [];

            for (let i = 0; i < acceptedFiles.length; i += batchSize) {
                const batchFiles = acceptedFiles.slice(i, i + batchSize);
                // eslint-disable-next-line no-loop-func
                const batchPromises = batchFiles.map(file =>
                    processFile(file).then(result => {
                        completedOperations++;
                        setUploading(
                            calculatePercentual(
                                completedOperations,
                                numberOfOperations
                            )
                        );
                        return result;
                    })
                );
                // Use settled pattern (ES2017-compatible) so a single file failure
                // does not abort the entire batch. Wraps each promise to always resolve
                // with a discriminated union {ok: true, value} | {ok: false, reason}.
                type Settled<T> =
                    | { ok: true; value: T }
                    | { ok: false; reason: any };
                const settledPromises = batchPromises.map(p =>
                    p
                        .then(value => ({ ok: true as const, value }))
                        .catch(reason => ({ ok: false as const, reason }))
                );
                const batchSettled: Settled<any>[] =
                    await Promise.all(settledPromises);
                const batchSucceeded = batchSettled
                    .filter((r): r is { ok: true; value: any } => r.ok === true)
                    .map(r => r.value);
                const batchFailed = batchSettled.filter(r => r.ok === false);
                // Update progress counter for files that failed (their .then() never ran)
                batchFailed.forEach(() => {
                    completedOperations++;
                    setUploading(
                        calculatePercentual(
                            completedOperations,
                            numberOfOperations
                        )
                    );
                });
                failedFilesCount += batchFailed.length;
                results.push(...batchSucceeded);
            }

            if (failedFilesCount > 0) {
                showMessage({
                    message: t("UploadPartialFailure", {
                        count: failedFilesCount,
                        defaultValue: `${failedFilesCount} arquivo(s) não puderam ser processados e foram ignorados.`
                    }),
                    snackBarOrign: { vertical: "top", horizontal: "right" },
                    timeout: 1000 * 10,
                    typeMessage: "warning"
                });
            }

            if (results.length === 0) {
                // All files failed — nothing to submit
                showMessage({
                    message: t("NotIdentifiedProblem"),
                    snackBarOrign: { vertical: "top", horizontal: "right" },
                    timeout: 1000 * 10,
                    typeMessage: "error"
                });
                setUploading(false);
                return;
            }

            valueDefinition.push(...results);

            const body = {
                parsedItems: valueDefinition,
                companyId: company.companyId,
                source: "site"
            };
            const axiosReturnSendData = await apiDirectCall(
                "/parser/parseruploadstart",
                "post",
                body
            );
            setUploading(100);
            const postData = axiosReturnSendData.data;
            const operationId = postData.OPERATIONID;
            const waitController = parserWaitController.current;
            if (waitController.signal.aborted) {
                return;
            }

            setWaitingForParser(true);
            try {
                const parserItems = await waitForParserUploadResults({
                    expectedItemCount: results.length,
                    signal: waitController.signal,
                    loadItems: async ({ signal, timeoutMs }) => {
                        const result = await apiDirectCall(
                            "/parser/parseroperationdetaillist",
                            "post",
                            {
                                companyId: company.companyId,
                                operationId,
                                sizeProcessLocal: results.length + 1
                            },
                            { signal, timeout: timeoutMs }
                        );

                        return Array.isArray(result.data?.data)
                            ? result.data.data
                            : [];
                    }
                });
                if (parserItems === undefined) {
                    return;
                }
            } catch (error) {
                if (waitController.signal.aborted) {
                    return;
                }
                const parserTimedOut =
                    error instanceof ParserUploadTimeoutError;
                showMessage({
                    message: parserTimedOut
                        ? t("ParserProcessingTimeout")
                        : t("NotIdentifiedProblem"),
                    snackBarOrign: { vertical: "top", horizontal: "right" },
                    timeout: 1000 * 10,
                    typeMessage: parserTimedOut ? "warning" : "error"
                });
                navigate("/applicant/parserlist");
                setWaitingForParser(false);
                setUploading(false);
                return;
            }

            navigate(`/applicant/parserlistdetail/${operationId}`, {
                state: { parserUploadReady: true }
            });
            setWaitingForParser(false);
            setUploading(false);
        } catch (ex) {
            if (parserWaitController.current.signal.aborted) {
                return;
            }
            console.log("ex", ex);
            showMessage({
                message: t("NotIdentifiedProblem"),
                snackBarOrign: { vertical: "top", horizontal: "right" },
                timeout: 1000 * 10,
                typeMessage: "error"
            });
            setUploading(false);
        }
    };

    const newMetadata = { ...metadata, elementType: "multipleFiles" };
    if (waitingForParser) {
        return <Loading />;
    }

    const pageReady = !uploading;
    if (pageReady) {
        return (
            <Grid item xs={12}>
                <Field
                    metadata={newMetadata}
                    type="text"
                    name={field.name}
                    label={t(field.name)}
                    helperTextDefault={t(`${field.name}_helperText`, "")}
                    fullWidth
                    className={props.className}
                    component={File}
                    required={requiredMetadata}
                    t={t}
                    onDropFunction={onDropFunction}
                />
                <AlertUpgradePlan feature="bulkParser" />
            </Grid>
        );
    } else {
        //@ts-expect-error uploading is narrowed by the pageReady branch.
        if (uploading === false) {
            return <Loading />;
        } else {
            const progress: number = Number(uploading);
            return <LinearProgressWithLabel value={progress} />;
        }
    }
};

export default UploadComponent;
